<script setup lang="ts">
/**
 * [#e03fdbc2] Лендинг. Главная мысль страницы: график платежей управляет телефоном.
 * Зелёный — оплачено, оранжевый — просрочка, красный — заблокирован; цвета идут через все блоки.
 * Переключатель в первом экране только меняет картинку, команд устройствам здесь нет.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue';
import {
  ArrowRight, Building2, Check, ChevronRight, Clock, FileText, HardDriveDownload, Handshake, Info, Landmark, LayoutDashboard, Lock, Mail, MapPin,
  LockOpen, RotateCcw, Send, ShieldCheck, Store, Tag, Wallet, Wifi, WifiOff,
} from 'lucide-vue-next';
import { TELEGRAM_HANDLE, TELEGRAM_URL } from '@/seo/site';
import ContactBlock from './landing/ContactBlock.vue';
import { TRUSTED_PARTNERS } from './landing/partners';
import { BRAND_PATHS } from './landing/brands';
import DeviceMock from './landing/DeviceMock.vue';
import PhoneMock from './landing/PhoneMock.vue';
import { useNow } from './landing/useNow';
import PublicShell from './landing/PublicShell.vue';
import { usePublicPage } from './landing/usePublicPage';
import type { LockState } from './landing/copy';

const { copy, more, to } = usePublicPage();

const STATES: LockState[] = ['paid', 'soft', 'locked'];
const state = ref<LockState>('locked');
const stateIndex = computed(() => STATES.indexOf(state.value));
let timer: ReturnType<typeof setInterval> | null = null;
let startTimer: ReturnType<typeof setTimeout> | null = null;

function stopAuto(): void {
  if (startTimer) clearTimeout(startTimer);
  if (timer) clearInterval(timer);
  timer = null;
}
function pick(s: LockState): void {
  stopAuto();
  state.value = s;
}
onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // Смена экранов начинается не сразу: первый кадр спокойно читается и быстрее считается загруженным.
  startTimer = setTimeout(() => {
    timer = setInterval(() => {
      state.value = STATES[(stateIndex.value + 1) % STATES.length];
    }, 3600);
  }, 9000);
});
onUnmounted(stopAuto);

// Точки под тремя телефонами: на мобиле блок листается вбок.
const statesEl = ref<HTMLElement | null>(null);
const statesPage = ref(0);
function onStatesScroll(): void {
  const el = statesEl.value;
  if (!el) return;
  const max = el.scrollWidth - el.clientWidth;
  statesPage.value = max > 0 ? Math.round((el.scrollLeft / max) * (STATES.length - 1)) : 0;
}

// Кнопка заявки снизу на мобиле: после первого экрана и пока форма не на экране.
const stickyCta = ref(false);
function onPageScroll(): void {
  const form = document.getElementById('contact')?.getBoundingClientRect();
  stickyCta.value = window.scrollY > 700 && !!form && form.top > window.innerHeight;
}
onMounted(() => window.addEventListener('scroll', onPageScroll, { passive: true }));
onUnmounted(() => window.removeEventListener('scroll', onPageScroll));

/** Статус шести платежей в карточке графика: четвёртый платёж решает всё. */
const payments = computed(() =>
  copy.value.schedule.months.map((label, i) => {
    if (i < 3) return { label, tone: 'paid' };
    if (i === 3) return { label, tone: state.value };
    return { label, tone: 'due' };
  }),
);
const currentStatus = computed(() => {
  const s = copy.value.schedule;
  return state.value === 'paid' ? s.paid : s.late;
});

const whyIcons = [RotateCcw, HardDriveDownload, MapPin, LayoutDashboard];

const whoIcons = [Store, Wallet, Landmark];
const { time, date, minutesAgo } = useNow();
// Примеры уведомлений идут по порядку и заканчиваются почти сейчас.
const EVENT_MINUTES_AGO = [190, 125, 47, 6];
function eventMeta(meta: string, index: number): string {
  const at = minutesAgo(EVENT_MINUTES_AGO[index] ?? 0);
  return at ? meta.replace(/\d{1,2}:\d{2}$/, at) : meta;
}

const eventIcon = { paid: ShieldCheck, locked: Lock, info: LockOpen, soft: RotateCcw };

const deviceList = computed(() => [
  { name: 'iPhone', kind: 'iphone', by: more.value.devices.bySerial },
  { name: 'iPad', kind: 'ipad', by: more.value.devices.bySerial },
  { name: 'Android', kind: 'android', by: more.value.devices.byImei },
  { name: 'Samsung', kind: 'samsung', by: more.value.devices.byImei },
]);
</script>

<template>
  <PublicShell>
    <main>
      <section class="lp-hero lp-wrap" :data-state="state">
        <div class="lp-hero-text">
          <h1>{{ copy.hero.title }} <span>{{ copy.hero.accent }}</span></h1>
          <p>{{ copy.hero.sub }}</p>
          <div class="lp-actions">
            <a href="#contact" class="lp-btn lp-btn--primary">{{ copy.hero.cta }}<ArrowRight :size="17" /></a>
            <a href="#how" class="lp-btn">{{ copy.hero.more }}</a>
          </div>
          <div class="lp-chips">
            <span>{{ more.devicesLabel }}</span>
            <ul><li v-for="d in deviceList" :key="d.name">{{ d.name }}</li></ul>
          </div>
        </div>

        <div class="lp-stage">
          <div class="lp-glow" />
          <PhoneMock class="lp-stage-phone" :state="state" :text="copy.phone" />
          <div class="lp-plan lp-glass" aria-hidden="true">
            <div class="lp-plan-head">
              <strong>{{ copy.schedule.title }}</strong>
              <span>{{ copy.schedule.contract }}</span>
            </div>
            <ol class="lp-plan-bar">
              <li v-for="p in payments" :key="p.label" :data-tone="p.tone"><i /><span>{{ p.label }}</span></li>
            </ol>
            <div class="lp-plan-row">
              <span>{{ copy.schedule.amount }}</span>
              <b :data-tone="state">{{ currentStatus }}</b>
            </div>
          </div>
          <div class="lp-control">
            <div class="lp-seg lp-glass" role="radiogroup" :aria-label="copy.hero.stateLabel" :style="{ '--i': stateIndex }">
              <span class="lp-seg-thumb" />
              <button v-for="s in STATES" :key="s" type="button" role="radio" :aria-checked="state === s" @click="pick(s)">{{ copy.states[s].tab }}</button>
            </div>
            <p class="lp-caption"><i :data-tone="state" />{{ copy.states[state].caption }}</p>
          </div>
        </div>
      </section>

      <section class="lp-trust" :aria-label="more.trusted">
        <h2>{{ more.trusted }}</h2>
        <div class="lp-marquee">
          <ul>
            <li v-for="name in TRUSTED_PARTNERS" :key="name">{{ name }}</li>
          </ul>
          <ul aria-hidden="true">
            <li v-for="name in TRUSTED_PARTNERS" :key="name">{{ name }}</li>
          </ul>
        </div>
      </section>

      <section class="lp-about lp-wrap">
        <h2>{{ more.about.title }}</h2>
        <p>{{ more.about.text }}</p>
      </section>

      <section id="who" class="lp-section lp-section--tint">
        <div class="lp-wrap">
          <header class="lp-head"><h2>{{ more.who.title }}</h2><p>{{ more.who.sub }}</p></header>
          <ul class="lp-three">
            <li v-for="(w, i) in more.who.items" :key="w.title" class="lp-card">
              <span class="lp-tile lp-who-tile"><component :is="whoIcons[i]" :size="18" /></span>
              <h3>{{ w.title }}</h3>
              <p>{{ w.text }}</p>
            </li>
          </ul>
          <p class="lp-note">{{ more.who.partnerLine }} <RouterLink :to="to('/partners')" class="lp-inline">{{ more.who.partnerLink }}</RouterLink></p>
        </div>
      </section>

      <section id="devices" class="lp-section lp-wrap">
        <header class="lp-head"><h2>{{ more.devices.title }}</h2><p>{{ more.devices.sub }}</p></header>
        <ul class="lp-devices">
          <li v-for="d in deviceList" :key="d.name" class="lp-card">
            <div class="lp-device-view"><DeviceMock :kind="d.kind" :label="more.devices.status" /></div>
            <h3>
              {{ d.name }}
              <svg class="lp-brand-mark" :data-kind="d.kind" viewBox="0 0 24 24" aria-hidden="true"><path :d="BRAND_PATHS[d.kind]" /></svg>
            </h3>
            <p>{{ d.by }}</p>
          </li>
        </ul>
        <p class="lp-note">{{ more.devices.note }}</p>
      </section>

      <section id="how" class="lp-section lp-section--tint">
        <div class="lp-wrap">
          <header class="lp-head"><h2>{{ copy.how.title }}</h2><p>{{ copy.how.sub }}</p></header>
          <ol class="lp-three">
            <li v-for="(s, i) in copy.how.steps" :key="s.title" class="lp-card">
              <span class="lp-num">{{ i + 1 }}</span>
              <h3>{{ s.title }}</h3>
              <p>{{ s.text }}</p>
            </li>
          </ol>
        </div>
      </section>

      <section id="lock" class="lp-section lp-wrap">
        <header class="lp-head"><h2>{{ copy.lock.title }}</h2><p>{{ copy.lock.sub }}</p></header>
        <div ref="statesEl" class="lp-states" @scroll.passive="onStatesScroll">
          <article v-for="s in STATES" :key="s" class="lp-card lp-state" :data-state="s">
            <div class="lp-state-view"><PhoneMock :state="s" :text="copy.phone" /></div>
            <h3><i :data-tone="s" />{{ copy.lock.cards[s].title }}</h3>
            <p>{{ copy.lock.cards[s].text }}</p>
            <ul>
              <li v-for="w in copy.lock.cards[s].works" :key="w"><Check :size="15" />{{ w }}</li>
            </ul>
          </article>
        </div>
        <div class="lp-dots" aria-hidden="true"><i v-for="(s, i) in STATES" :key="s" :data-on="i === statesPage" /></div>
        <p class="lp-note">{{ copy.lock.note }}</p>
      </section>

      <section id="billing" class="lp-section lp-section--tint">
        <div class="lp-wrap">
          <header class="lp-head"><h2>{{ copy.flow.title }}</h2><p>{{ copy.flow.sub }}</p></header>
          <ol class="lp-flow">
            <li v-for="f in copy.flow.items" :key="f.when" :data-tone="f.tone">
              <i />
              <h3>{{ f.when }}</h3>
              <p>{{ f.text }}</p>
            </li>
          </ol>
        </div>
      </section>

      <section class="lp-section lp-wrap">
        <header class="lp-head"><h2>{{ copy.why.title }}</h2><p>{{ copy.why.sub }}</p></header>
        <ul class="lp-list lp-list--two">
          <li v-for="(w, i) in copy.why.items" :key="w.title">
            <span class="lp-tile"><component :is="whyIcons[i]" :size="18" /></span>
            <div><h3>{{ w.title }}</h3><p>{{ w.text }}</p></div>
          </li>
        </ul>
      </section>

      <section id="price" class="lp-section lp-section--tint">
        <div class="lp-wrap">
          <header class="lp-head"><h2>{{ more.terms.title }}</h2></header>
          <ul class="lp-three">
            <li class="lp-card lp-term">
              <span class="lp-tile"><Tag :size="18" /></span>
              <h3>{{ more.terms.price.title }}</h3>
              <strong>{{ more.terms.price.big }}</strong>
              <p>{{ more.terms.price.text }}</p>
              <a :href="TELEGRAM_URL" class="lp-link" rel="noopener"><Send :size="16" />{{ more.terms.price.contact }}</a>
            </li>
            <li class="lp-card lp-term">
              <span class="lp-tile"><FileText :size="18" /></span>
              <h3>{{ more.terms.contract.title }}</h3>
              <strong>{{ more.terms.contract.big }}</strong>
              <p>{{ more.terms.contract.text }}</p>
              <RouterLink :to="to('/contract')" class="lp-link">{{ more.terms.contract.link }}<ArrowRight :size="16" /></RouterLink>
            </li>
            <li class="lp-card lp-term">
              <span class="lp-tile"><Clock :size="18" /></span>
              <h3>{{ more.terms.support.title }}</h3>
              <strong class="lp-tabular">{{ more.terms.support.hours }}</strong>
              <p>{{ more.terms.support.days }}</p>
              <a :href="TELEGRAM_URL" class="lp-link" rel="noopener"><Send :size="16" />{{ TELEGRAM_HANDLE }}</a>
            </li>
          </ul>
          <div class="lp-apple">
            <div class="lp-apple-head"><span>{{ more.apple.badge }}</span><h3>{{ more.apple.title }}</h3></div>
            <div class="lp-apple-stat">
              <strong>{{ more.apple.items[0].title }}</strong>
              <div class="lp-week" aria-hidden="true"><i v-for="n in 7" :key="n" /></div>
              <p>{{ more.apple.items[0].text }}</p>
            </div>
            <div class="lp-apple-stat">
              <strong>{{ more.apple.items[1].title }}</strong>
              <div class="lp-term-scale" aria-hidden="true"><span>1</span><div><i /></div><span>36</span></div>
              <p>{{ more.apple.items[1].text }}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="reports" class="lp-section lp-wrap lp-report">
        <div>
          <header class="lp-head"><h2>{{ more.reports.title }}</h2><p>{{ more.reports.sub }}</p></header>
          <p class="lp-alert"><span class="lp-badge">{{ more.reports.alertBadge }}</span>{{ more.reports.alert }}</p>
        </div>
        <div class="lp-lockscreen">
          <div class="lp-lockscreen-time" aria-hidden="true"><span>{{ date }}</span><b>{{ time }}</b></div>
          <ul class="lp-events">
            <li v-for="(e, i) in more.reports.events" :key="e.title" :data-event="e.tone">
              <span class="lp-tile"><component :is="eventIcon[e.tone]" :size="18" /></span>
              <div><b>{{ e.title }}</b><small>{{ eventMeta(e.meta, i) }}</small></div>
            </li>
          </ul>
        </div>
      </section>

      <section id="offline" class="lp-section lp-wrap lp-report lp-report--flip">
        <div>
          <header class="lp-head"><h2>{{ more.offline.title }}</h2><p>{{ more.offline.text }}</p></header>
          <p class="lp-alert"><span class="lp-badge">{{ more.reports.alertBadge }}</span>{{ more.offline.alert }}</p>
        </div>
        <div class="lp-lockscreen lp-lockscreen--alert">
          <div class="lp-lockscreen-time" aria-hidden="true"><span>{{ date }}</span><b>{{ time }}</b></div>
          <ul class="lp-events">
            <li v-for="(r, i) in more.offline.rows" :key="r.name" :data-event="r.tone">
              <span class="lp-tile"><component :is="i === 0 ? Wifi : WifiOff" :size="18" /></span>
              <div class="lp-grow"><b>{{ r.name }}</b><small>{{ r.status }}</small></div>
              <em v-if="i === more.offline.rows.length - 1">{{ more.offline.action }}</em>
            </li>
          </ul>
        </div>
      </section>

      <section id="backup" class="lp-wrap lp-backup-wrap">
        <div class="lp-card lp-backup">
          <div class="lp-backup-view" aria-hidden="true">
            <span class="dv" data-kind="iphone"><HardDriveDownload :size="30" /></span>
            <ArrowRight :size="30" />
            <span class="dv dv--safe" data-kind="iphone"><ShieldCheck :size="30" /></span>
          </div>
          <div>
            <span class="lp-badge">{{ more.reports.backup.badge }}</span>
            <h2>{{ more.reports.backup.title }}</h2>
            <p>{{ more.reports.backup.text }}</p>
            <p class="lp-limit"><Info :size="16" />{{ more.reports.backup.limit }}</p>
            <RouterLink :to="to('/faq/backup')" class="lp-link">{{ more.reports.backup.link }}<ArrowRight :size="16" /></RouterLink>
          </div>
        </div>
      </section>

      <section id="services" class="lp-section lp-section--tint">
        <div class="lp-wrap">
        <header class="lp-head"><h2>{{ more.services.title }}</h2><p>{{ more.services.sub }}</p></header>
        <ul class="lp-list">
          <li>
            <RouterLink :to="to('/abm')" class="lp-row">
              <span class="lp-tile"><Building2 :size="18" /></span>
              <div class="lp-grow"><h3>{{ more.services.abm.title }}</h3><p>{{ more.services.abm.text }}</p></div>
              <ChevronRight :size="18" />
            </RouterLink>
          </li>
          <li>
            <RouterLink :to="to('/abm')" class="lp-row">
              <span class="lp-tile"><Mail :size="18" /></span>
              <div class="lp-grow"><h3>{{ more.services.mail.title }}</h3><p>{{ more.services.mail.text }}</p></div>
              <ChevronRight :size="18" />
            </RouterLink>
          </li>
        </ul>
        </div>
      </section>

      <section class="lp-wrap lp-band-wrap">
        <RouterLink :to="to('/partners')" class="lp-band">
          <span class="lp-tile lp-tile--big"><Handshake :size="26" /></span>
          <div class="lp-grow"><h2>{{ more.partnerBand.title }}</h2><p>{{ more.partnerBand.text }}</p></div>
          <span class="lp-btn">{{ more.partnerBand.link }}<ArrowRight :size="17" /></span>
        </RouterLink>
      </section>

      <section id="faq" class="lp-section lp-wrap">
        <header class="lp-head"><h2>{{ copy.faq.title }}</h2></header>
        <div class="lp-list">
          <details v-for="f in copy.faq.items" :key="f.q" class="lp-faq">
            <summary><h3>{{ f.q }}</h3><ChevronRight :size="18" /></summary>
            <p>{{ f.a }}</p>
          </details>
        </div>
        <RouterLink :to="to('/articles')" class="lp-link">{{ copy.guides.all }}<ArrowRight :size="16" /></RouterLink>
      </section>

      <section id="api" class="lp-section lp-section--tint">
        <div class="lp-wrap lp-api">
          <div>
            <header class="lp-head"><h2>{{ more.api.title }}</h2><p>{{ more.api.text }}</p></header>
            <p class="lp-api-docs">{{ more.api.docs }}</p>
            <a href="/login?next=/app/developers" class="lp-link">{{ more.api.link }}<ArrowRight :size="16" /></a>
          </div>
          <div class="lp-code" aria-hidden="true">
            <div class="lp-code-bar"><i /><i /><i /><span>DWISH MDM API</span></div>
            <ul>
              <li><b>POST</b><code>/api/v1/devices</code><span>{{ more.api.enroll }}</span></li>
              <li><b>POST</b><code>/api/v1/devices/:id/commands</code><span>{{ more.api.command }}</span></li>
              <li><b data-kind="hook">HOOK</b><code>device.status_changed</code><span>{{ more.api.callback }}</span></li>
            </ul>
          </div>
        </div>
      </section>

      <ContactBlock />
    </main>
    <a href="#contact" class="lp-btn lp-btn--primary lp-sticky" :data-show="stickyCta">{{ copy.hero.cta }}<ArrowRight :size="17" /></a>
  </PublicShell>
</template>
